import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PlusIcon } from 'lucide-react';

import useUpdateProfile from 'hooks/useUpdateProfile';
import { cn } from 'lib/utils';
import { PATH_ROUTES } from 'routes/path';
import { MemberDetail } from 'services/profile/type';
import useAuthStore from 'store/auth';
import { buttonVariants } from './ui/button';
import { Input } from './ui/input';

import ProfileImage from 'assets/images/avatar.png';

interface FamilyMemberProps {
  onChange?: () => void;
  setIsLoading: (loading: boolean) => void;
}

const variants = {
  open: {
    transition: { staggerChildren: 0.07, delayChildren: 0.2 },
  },
  closed: {
    transition: { staggerChildren: 0.05, staggerDirection: -1 },
  },
};

const memberVariants = {
  open: {
    y: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 },
    },
  },
  closed: {
    y: 50,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 },
    },
  },
};

export function FamilyMember({ onChange, setIsLoading }: FamilyMemberProps) {
  const { t } = useTranslation();

  const memberDetails = useAuthStore((store) => store.memberDetails);
  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const { onUpdateUserAttributes } = useUpdateProfile();

  const [user, setUser] = useState<string>('');
  const [members, setMembers] = useState<MemberDetail[]>([]);

  useEffect(() => {
    const familyMembers = memberDetails.sort((a, b) =>
      a.relation === 'Admin' ? -1 : b.relation === 'Admin' ? 1 : 0
    );
    setMembers(familyMembers);
  }, [memberDetails]);

  const onProfileChange = async (profileId: string) => {
    setIsLoading(true);
    await onUpdateUserAttributes(profileId);
    onChange?.();
    setIsLoading(false);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setUser(value);
    const filteredMembers = memberDetails.filter(
      ({ given_name, family_name }) =>
        `${given_name} ${family_name}`
          .toLowerCase()
          .includes(value.toLowerCase())
    );
    setMembers(filteredMembers);
  };

  return (
    <div className='px-2 py-4 space-y-4'>
      <div>
        <Input
          placeholder={t('search')}
          value={user}
          onChange={onSearch}
          className='px-4 pt-1 border-transparent rounded-full'
        />
      </div>
      <div className='space-y-4'>
        <Link
          to={`${PATH_ROUTES.ADD_MEMBER}?redirectTo=${PATH_ROUTES.PROFILE}`}
          className={buttonVariants({ className: 'w-full' })}
        >
          <PlusIcon className='mr-2 size-4' />
          <span>{t('add_new_member')}</span>
        </Link>

        {members?.length ? (
          <motion.div
            variants={variants}
            initial='closed'
            animate='open'
            exit='closed'
            className='space-y-4'
          >
            {members?.map(
              ({ profile_id, family_name, given_name, relation }) => {
                const isSelected = profile_id === profileId;

                return (
                  <motion.div
                    className={cn(
                      'flex items-center px-4 py-2 gap-x-4 rounded-xl cursor-pointer transition-colors duration-200 hover:bg-white/30',
                      isSelected ? 'bg-white/90' : 'bg-white/10'
                    )}
                    onClick={() => onProfileChange(profile_id)}
                    key={profile_id}
                    variants={memberVariants}
                  >
                    <div className='size-12 aspect-square'>
                      <img
                        src={ProfileImage}
                        alt='profile image'
                        className='object-cover w-full h-full'
                      />
                    </div>
                    <div>
                      <h2
                        className={cn(
                          'font-semibold',
                          isSelected ? 'text-gray-900' : 'text-white'
                        )}
                      >
                        {given_name} {family_name}
                      </h2>

                      {relation === 'Admin' && (
                        <p className='text-sm font-medium text-primary'>
                          {t('primary_account')}
                        </p>
                      )}
                    </div>
                  </motion.div>
                );
              }
            )}
          </motion.div>
        ) : (
          <div>
            <p className='font-medium text-center text-white'>
              {t('no_member_found')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
